import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	Badge,
	Box,
	Button,
	Container,
	Icons,
	Inline,
	RelativeTime,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	CheckboxInputField,
	DateRangePickerField,
	SelectField,
	SelectInputField,
} from "@sembark-travel/ui-form"
import {
	ListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect, useState } from "react"
import {
	MonthNavigation,
	MonthlyAccountsTable,
	MonthlyUsersTable,
	SelectTenants,
	TenantBillingForMonth,
} from "../Tenants"
import config from "../../config"
import { TemporaryStorage } from "../../DownloadManager"
import { generatePath } from "../../router-utils"
import { SelectUsers } from "../../Users"
import {
	TChurnRisk,
	TChurnRiskRow,
	TMonthlyTenantRow,
	TMonthlyUserRow,
	TMonthlyUserStatus,
	TMonthlyUsersResponse,
	tenantReportsXHR,
} from "./store"
import {
	CHURN_RISK_VIEWS,
	TTenantFilters,
	TTenantFiltersInQuery,
	churnRisksFor,
	formatChange,
	queryToTenantFilters,
	tenantFiltersToQuery,
	tenantFiltersToRequest,
} from "./utils"

type TMonthlyFilters = TSearchParams &
	TTenantFilters & { month: Date; outside_india?: boolean }
type TMonthlyQuery = TSearchParams &
	TTenantFiltersInQuery & { month?: string; oi?: 1 }

/**
 * Users per tenant for a month: added and deleted by part of the month
 * (before the 10th, 10th–19th, from the 20th), short-lived accounts and
 * logins from outside India.
 */
export function MonthlyTenantReport() {
	const [query, setQuery] = useLocationQuery<TMonthlyFilters, TMonthlyQuery>({
		toQuery: ({ q, page, month, outside_india, ...filters }) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			month: formatDate(month || new Date(), "YYYY-MM"),
			...(outside_india ? { oi: 1 as const } : {}),
			...tenantFiltersToQuery(filters),
		}),
		fromQuery: ({ q, page, month, oi, ...query }) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			month: month
				? startOf(parseDateFromQuery(`${month}-01`), "month")
				: startOf(new Date(), "month"),
			...(oi ? { outside_india: true } : {}),
			...queryToTenantFilters(query),
		}),
	})
	const [params, setParams] = useSearch<TMonthlyFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { month } = params
	return (
		<Search
			title="Monthly Accounts"
			placeholder="Search tenants..."
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			resetParams={(params) => ({ q: "", page: 1, month: params.month })}
			Filters={MonthlyFilters}
			areAdvancedFiltersApplied={(params) => {
				// the month is always set and has its own arrows, not a filter
				const { month, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ searchParams, setSearchParams }) => (
				<MonthArrows
					month={searchParams.month}
					onChange={(month) => setSearchParams({ month, page: 1 })}
				/>
			)}
		>
			<ListView<TMonthlyTenantRow, TMonthlyFilters>
				pageKey="tenant-reports-monthly"
				params={params}
				fetch={(xhr, params) =>
					tenantReportsXHR(xhr).getMonthly({
						...tenantFiltersToRequest(params),
						month: formatDate(params.month, "YYYY-MM"),
						outside_india: params.outside_india ? 1 : undefined,
						q: params.q || undefined,
						page: params.page,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => (
					<MonthlyAccountsTable
						rows={items}
						month={month}
						renderTenant={(row) => <TenantLink tenant={row.tenant} />}
						renderActions={(row) => (
							<BillingDetailsInDialog row={row} month={month} />
						)}
					/>
				)}
			</ListView>
		</Search>
	)
}

type TMonthlyUsersFilters = TSearchParams &
	TTenantFilters & {
		month: Date
		tenants?: Array<{ id: number; name: string }>
		user_created_after?: Date
		user_created_before?: Date
		outside_india?: boolean
		status?: TMonthlyUserStatus
	}
type TMonthlyUsersQuery = TSearchParams &
	TTenantFiltersInQuery & {
		month?: string
		/** "id__name", as on the auth logs page */
		tenants?: Array<string>
		/** user created between */
		uca?: string
		ucb?: string
		oi?: 1
		status?: TMonthlyUserStatus
	}

const MONTHLY_USER_STATUSES: Array<[TMonthlyUserStatus, string]> = [
	["added", "Added this month"],
	["deleted", "Deleted this month"],
	["short_lived", "Short-lived (15 days or fewer)"],
	["no_logins", "No logins this month"],
]

/**
 * Tenant users of a month, as in a tenant's billing details but across the
 * tenants: days in the month, logins, logins from outside India and a cell
 * per day. By tenant, then name.
 */
export function MonthlyUsersReport() {
	const [query, setQuery] = useLocationQuery<
		TMonthlyUsersFilters,
		TMonthlyUsersQuery
	>({
		toQuery: ({
			q,
			page,
			month,
			tenants,
			user_created_after,
			user_created_before,
			outside_india,
			status,
			...filters
		}) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			month: formatDate(month || new Date(), "YYYY-MM"),
			...(tenants?.length
				? { tenants: tenants.map((t) => `${t.id}__${t.name}`) }
				: {}),
			...(user_created_after ? { uca: dateToQuery(user_created_after) } : {}),
			...(user_created_before ? { ucb: dateToQuery(user_created_before) } : {}),
			...(outside_india ? { oi: 1 as const } : {}),
			...(status ? { status } : {}),
			...tenantFiltersToQuery(filters),
		}),
		fromQuery: ({
			q,
			page,
			month,
			tenants,
			uca,
			ucb,
			oi,
			status,
			...query
		}) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			month: month
				? startOf(parseDateFromQuery(`${month}-01`), "month")
				: startOf(new Date(), "month"),
			...(tenants?.length
				? {
						tenants: tenants.map((t) => {
							const [id, ...name] = t.split("__")
							return { id: Number(id), name: name.join("__") }
						}),
					}
				: {}),
			...(uca ? { user_created_after: parseDateFromQuery(uca) } : {}),
			...(ucb ? { user_created_before: parseDateFromQuery(ucb) } : {}),
			...(oi ? { outside_india: true } : {}),
			...(status ? { status } : {}),
			...queryToTenantFilters(query),
		}),
	})
	const [params, setParams] = useSearch<TMonthlyUsersFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { month } = params
	return (
		<Search
			title="Monthly Accounts"
			placeholder="Search users by name or email..."
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			resetParams={(params) => ({ q: "", page: 1, month: params.month })}
			Filters={MonthlyUsersFilters}
			areAdvancedFiltersApplied={(params) => {
				// the month is always set and has its own arrows, not a filter
				const { month, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ searchParams, setSearchParams }) => (
				<Inline alignItems="center" gap="4">
					<MonthArrows
						month={searchParams.month}
						onChange={(month) => setSearchParams({ month, page: 1 })}
					/>
					<DownloadMonthlyUsers params={params} />
				</Inline>
			)}
		>
			<ListView<TMonthlyUserRow, TMonthlyUsersFilters>
				pageKey="tenant-reports-monthly-users"
				params={params}
				fetch={(xhr, params) =>
					tenantReportsXHR(xhr).getMonthlyUsers({
						...monthlyUsersRequest(params),
						page: params.page,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, meta }) => (
					<MonthlyUsersTable<TMonthlyUserRow>
						month={month}
						users={items}
						logins={(meta as TMonthlyUsersResponse["meta"]).logins}
						renderTenant={(user) => (
							<TenantLink tenant={user.tenant} maxWidth="150px" />
						)}
						daysInMonth={(user) => user.days_in_month}
						hideSerialAndId
						hideTotals
					/>
				)}
			</ListView>
		</Search>
	)
}

/**
 * Tenants, the tenant filters, when the user was created, the user's status
 * in the month and logins from outside India.
 */
function MonthlyUsersFilters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectTenants}
				name="tenants"
				label="Tenants"
				multiple
			/>
			<TenantFilters signupLabel="Tenant Signed Up Between" />
			<DateRangePickerField
				label="User Created Between"
				fromName="user_created_after"
				toName="user_created_before"
			/>
			<SelectInputField name="status" label="Status">
				<option value="">Any status</option>
				{MONTHLY_USER_STATUSES.map(([value, label]) => (
					<option key={value} value={value}>
						{label}
					</option>
				))}
			</SelectInputField>
			<CheckboxInputField name="outside_india" label="Outside India only" />
		</Stack>
	)
}

/**
 * The users report's filters as the API takes them, for the list and the
 * export.
 */
function monthlyUsersRequest(params: TMonthlyUsersFilters) {
	return {
		...tenantFiltersToRequest(params),
		month: formatDate(params.month, "YYYY-MM"),
		tenants: params.tenants?.length
			? params.tenants.map((t) => t.id)
			: undefined,
		// whole days, from the start of the first to the end of the last
		user_created_after: params.user_created_after
			? dateToUTCString(startOf(params.user_created_after, "day"))
			: undefined,
		user_created_before: params.user_created_before
			? dateToUTCString(endOf(params.user_created_before, "day"))
			: undefined,
		outside_india: params.outside_india ? (1 as const) : undefined,
		status: params.status || undefined,
		q: params.q || undefined,
	}
}

/**
 * Builds the users of the month (with the applied filters) as a
 * spreadsheet in the background, then offers it for download.
 */
function DownloadMonthlyUsers({ params }: { params: TMonthlyUsersFilters }) {
	return (
		<TemporaryStorage
			initUrl="/tenant-reports/monthly-users/export"
			initParams={{
				...monthlyUsersRequest(params),
				timezone_offset: config.timezoneOffset,
			}}
		>
			{({ generate, url, message, reset, isGenerating }) =>
				url ? (
					<Button
						as="a"
						href={url}
						target="_blank"
						download
						level="primary"
						onClick={() => reset()}
					>
						{message}
					</Button>
				) : (
					<Button onClick={generate} disabled={isGenerating}>
						{message || (
							<>
								<Icons.DocumentDownload /> Download
							</>
						)}
					</Button>
				)
			}
		</TemporaryStorage>
	)
}

/**
 * ‹ Sep 2026 › for the report's month.
 */
function MonthArrows({
	month,
	onChange,
}: {
	month: Date
	onChange: (month: Date) => void
}) {
	return (
		<Inline alignItems="center" gap="2">
			<Button
				title="Previous month"
				onClick={() => onChange(subtractUnit(month, 1, "month"))}
			>
				<Icons.ChevronDown rotate="90" />
			</Button>
			<Text fontWeight="semibold">{formatDate(month, "MMM YYYY")}</Text>
			<Button
				title="Next month"
				onClick={() => onChange(addUnit(month, 1, "month"))}
			>
				<Icons.ChevronDown rotate="270" />
			</Button>
		</Inline>
	)
}

/**
 * Button that opens the tenant's billing details for the month in a
 * dialog.
 */
function BillingDetailsInDialog({
	row,
	month,
}: {
	row: TMonthlyTenantRow
	month: Date
}) {
	const [open, setOpen] = useState(false)
	// the dialog browses months on its own; the report keeps its month
	const [dialogMonth, setDialogMonth] = useState(month)
	return (
		<>
			<Button
				size="sm"
				title="Show billing details"
				level="tertiary"
				onClick={() => {
					setDialogMonth(month)
					setOpen(true)
				}}
			>
				<Icons.ArrowExpand />
			</Button>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				title={`${row.tenant.name}: ${formatDate(dialogMonth, "MMMM YYYY")}`}
				fitContainer
			>
				<Dialog.Body>
					<Stack gap="4">
						<MonthNavigation month={dialogMonth} onChange={setDialogMonth} />
						<TenantBillingForMonth
							tenantUid={row.tenant.uid}
							tenantId={row.tenant.id}
							month={dialogMonth}
						/>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type TChurnFilters = TSearchParams & TTenantFilters & { view?: string }
type TChurnQuery = TSearchParams & TTenantFiltersInQuery & { view?: string }

/**
 * Tenants likely to churn, riskiest first: weekly active users over the
 * last 4 weeks against the 8 before, recency and seat use.
 */
export function ChurnRiskReport() {
	const [query, setQuery] = useLocationQuery<TChurnFilters, TChurnQuery>({
		toQuery: ({ q, page, view, ...filters }) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			...(view ? { view } : {}),
			...tenantFiltersToQuery(filters),
		}),
		fromQuery: ({ q, page, view, ...query }) => ({
			...(q ? { q } : {}),
			...(page ? { page } : {}),
			...(view ? { view } : {}),
			...queryToTenantFilters(query),
		}),
	})
	const [params, setParams] = useSearch<TChurnFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search
			title="Churn Risk"
			placeholder="Search tenants..."
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			Filters={TenantFilters}
			areAdvancedFiltersApplied={(params) => {
				// the risk view has its own dropdown, not a filter
				const { view, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ searchParams, setSearchParams }) => (
				<SelectInput
					value={searchParams.view || "at_risk"}
					onChange={(e) =>
						setSearchParams({ view: e.currentTarget.value, page: 1 })
					}
				>
					{CHURN_RISK_VIEWS.map(([key, label]) => (
						<option key={key} value={key}>
							{label}
						</option>
					))}
				</SelectInput>
			)}
		>
			<Stack gap="4" paddingY="4">
				<Container>
					<Text fontSize="sm" color="muted">
						Weekly active users = distinct users who logged in that week. High:
						down 50% or more against the earlier 8 weeks, or nobody logged in
						for 14 days. Medium: down 25% or more, or under 30% of users active.
						Inactive: no logins at all in the last 12 weeks.
					</Text>
				</Container>
				<ListView<TChurnRiskRow, TChurnFilters>
					pageKey="tenant-reports-churn-risk"
					params={params}
					fetch={(xhr, params) =>
						tenantReportsXHR(xhr).getChurnRisk({
							...tenantFiltersToRequest(params),
							risks: churnRisksFor(params.view),
							q: params.q || undefined,
							page: params.page,
						})
					}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items }) => (
						<Table
							bordered
							responsive
							striped
							headers={[
								"Tenant",
								"Risk",
								"Weekly Active (last 4 wk / earlier 8 wk)",
								"Change",
								"Users Active",
								"Last Login",
								"Last 12 Weeks",
							]}
							rows={items.map((row) => [
								<Stack gap="px">
									<TenantLink tenant={row.tenant} />
									<Inline gap="1">
										{row.in_trial ? <Badge primary>Trial</Badge> : null}
										{row.on_hold ? <Badge warning>On hold</Badge> : null}
									</Inline>
								</Stack>,
								<RiskBadge risk={row.risk} />,
								<Text>
									{row.recent_weekly_active_users} /{" "}
									{row.baseline_weekly_active_users}
								</Text>,
								<Text
									fontWeight="semibold"
									color={
										row.trend !== null && row.trend < 0 ? "danger" : "success"
									}
								>
									{formatChange(row.trend)}
								</Text>,
								<Text>
									{row.seat_use === null
										? "-"
										: `${Math.round(row.seat_use * 100)}% of ${row.users}`}
								</Text>,
								row.last_login_at ? (
									<Stack gap="px">
										<RelativeTime timestamp={row.last_login_at} />
									</Stack>
								) : (
									<Text color="muted">Never</Text>
								),
								<Sparkline values={row.weekly_active_users} />,
							])}
						/>
					)}
				</ListView>
			</Stack>
		</Search>
	)
}

/**
 * Signed up between and supported by members, as on the tenants list.
 */
function TenantFilters({
	signupLabel = "Signed Up Between",
}: {
	/** e.g. "Tenant Signed Up Between" where users are listed */
	signupLabel?: string
}) {
	return (
		<Stack gap="4">
			<SelectField
				label="Supported By Member"
				select={SelectUsers}
				name="supported_by_users"
				multiple
				fetchOnMount
			/>
			<DateRangePickerField
				label={signupLabel}
				fromName="signup_after"
				toName="signup_before"
			/>
		</Stack>
	)
}

/**
 * The tenant filters plus tenants with logins from outside India in the
 * month.
 */
function MonthlyFilters() {
	return (
		<Stack gap="4">
			<TenantFilters />
			<CheckboxInputField name="outside_india" label="Outside India only" />
		</Stack>
	)
}

function TenantLink({
	tenant,
	maxWidth,
}: {
	tenant: { id: number; name: string }
	/** cut off longer names with an ellipsis; the full name shows on hover */
	maxWidth?: string
}) {
	return (
		<Link
			to={generatePath("/admin/tenants/:tenantId", {
				tenantId: tenant.id.toString(),
			})}
			color="accent"
			fontWeight="semibold"
			title={maxWidth ? tenant.name : undefined}
			style={
				maxWidth
					? {
							display: "inline-block",
							maxWidth,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							verticalAlign: "bottom",
						}
					: undefined
			}
		>
			{tenant.name}
		</Link>
	)
}

function RiskBadge({ risk }: { risk: TChurnRisk }) {
	switch (risk) {
		case "high":
			return <Badge danger>High</Badge>
		case "medium":
			return <Badge warning>Medium</Badge>
		case "low":
			return <Badge success>Low</Badge>
		case "new":
			return <Badge primary>New</Badge>
		default:
			return <Badge>Inactive</Badge>
	}
}

/**
 * Tiny bar chart of weekly active users, oldest first; the last 4 weeks
 * (the recent window) are drawn darker.
 */
function Sparkline({ values }: { values: Array<number> }) {
	const max = Math.max(1, ...values)
	return (
		<Inline gap="px" alignItems="end" title={values.join(", ")}>
			{values.map((value, index) => (
				<Box
					key={index}
					bgColor={index >= values.length - 4 ? "accent" : "subtle"}
					borderWidth="1"
					style={{
						width: "6px",
						height: `${Math.max(2, Math.round((value / max) * 24))}px`,
					}}
				/>
			))}
		</Inline>
	)
}
